import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  DocumentArrowUpIcon,
  ArrowDownTrayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import type { SourceSystem, Conversion } from '@types';
import { converterApi } from '@services/api';
import FileUploader from '@components/converter/FileUploader';
import ConversionStatusModal from '@components/converter/ConversionStatusModal';
import ConversionProgress from '@components/converter/ConversionProgress';

const POLL_INTERVAL = 2000; // Poll every 2 seconds

interface ActiveConversion {
  conversion: Conversion;
  file: File;
}

export default function Converter() {
  const [activeConversions, setActiveConversions] = useState<
    ActiveConversion[]
  >([]);
  const [selectedConversionId, setSelectedConversionId] = useState<
    string | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Track which conversions are actively being polled to avoid duplicate requests
  const pollingRef = useRef<Set<string>>(new Set());

  // Poll for status updates on active conversions
  useEffect(() => {
    const activeIds = activeConversions
      .filter(
        ac =>
          ac.conversion.status === 'PENDING' ||
          ac.conversion.status === 'PROCESSING'
      )
      .map(ac => ac.conversion.id);

    if (activeIds.length === 0) {
      return;
    }

    const pollStatus = async () => {
      const updates: { id: string; conversion: Conversion }[] = [];

      await Promise.all(
        activeIds.map(async id => {
          // Skip if already polling this ID
          if (pollingRef.current.has(id)) {
            return;
          }
          pollingRef.current.add(id);

          try {
            const updatedConversion =
              await converterApi.getConversionStatus(id);
            updates.push({ id, conversion: updatedConversion });
          } catch (error) {
            console.error(`Failed to poll status for conversion ${id}:`, error);
          } finally {
            pollingRef.current.delete(id);
          }
        })
      );

      if (updates.length > 0) {
        setActiveConversions(prev =>
          prev.map(ac => {
            const update = updates.find(u => u.id === ac.conversion.id);
            if (update) {
              // Show toast when conversion completes
              if (
                update.conversion.status === 'COMPLETED' &&
                ac.conversion.status !== 'COMPLETED'
              ) {
                toast.success(`${ac.file.name} converted successfully!`, {
                  icon: '✅',
                });
              } else if (
                update.conversion.status === 'FAILED' &&
                ac.conversion.status !== 'FAILED'
              ) {
                toast.error(
                  `${ac.file.name} failed: ${update.conversion.error_message || 'Unknown error'}`
                );
              }
              return { ...ac, conversion: update.conversion };
            }
            return ac;
          })
        );
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval
    const intervalId = setInterval(pollStatus, POLL_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [activeConversions]);

  const handleFilesSelected = useCallback(
    async (
      files: File[],
      sourceSystem: SourceSystem,
      mappingConfigId?: string
    ) => {
      setIsProcessing(true);

      try {
        const uploadPromises = files.map(async file => {
          try {
            const conversion = await converterApi.uploadFile({
              file,
              source_system: sourceSystem,
              mapping_config_id: mappingConfigId,
            });

            setActiveConversions(prev => [...prev, { conversion, file }]);

            toast.success(`Started converting ${file.name}`, {
              icon: '🚀',
            });

            return { success: true, conversion };
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to upload file';
            toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
            return { success: false, error: errorMessage };
          }
        });

        await Promise.all(uploadPromises);
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const handleViewStatus = (conversionId: string) => {
    setSelectedConversionId(conversionId);
    setIsModalOpen(true);
  };

  const handleDownload = async (conversionId: string) => {
    try {
      const blob = await converterApi.downloadConvertedFile(conversionId);
      const conversion = activeConversions.find(
        ac => ac.conversion.id === conversionId
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = conversion
        ? `converted_${conversion.file.name}`
        : `converted_file_${conversionId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('File downloaded successfully');

      // Remove from active conversions after download
      setActiveConversions(prev =>
        prev.filter(ac => ac.conversion.id !== conversionId)
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to download file';
      toast.error(errorMessage);
    }
  };

  const handleRetry = async (conversionId: string) => {
    try {
      const conversion = await converterApi.retryConversion(conversionId);

      // Update the conversion in active conversions
      setActiveConversions(prev =>
        prev.map(ac =>
          ac.conversion.id === conversionId ? { ...ac, conversion } : ac
        )
      );

      toast.success('Conversion restarted');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to retry conversion';
      toast.error(errorMessage);
    }
  };

  const handleCancelConversion = async (conversionId: string) => {
    try {
      await converterApi.cancelConversion(conversionId);

      // Update the conversion status locally
      setActiveConversions(prev =>
        prev.map(ac =>
          ac.conversion.id === conversionId
            ? { ...ac, conversion: { ...ac.conversion, status: 'CANCELLED' } }
            : ac
        )
      );

      toast.success('Conversion cancelled');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to cancel conversion';
      toast.error(errorMessage);
    }
  };

  const getConversionProgress = (conversion: Conversion): number => {
    switch (conversion.status) {
      case 'PENDING':
        return 10;
      case 'PROCESSING':
        if (conversion.estimated_records && conversion.record_count) {
          return Math.min(
            90,
            Math.floor(
              (conversion.record_count / conversion.estimated_records) * 100
            )
          );
        }
        return 50;
      case 'COMPLETED':
        return 100;
      case 'FAILED':
      case 'CANCELLED':
        return 0;
      default:
        return 0;
    }
  };

  const getStatusMessage = (conversion: Conversion): string => {
    switch (conversion.status) {
      case 'PENDING':
        return 'Queued for processing...';
      case 'PROCESSING':
        return 'Converting your file...';
      case 'COMPLETED':
        return 'Conversion completed';
      case 'FAILED':
        return conversion.error_message || 'Conversion failed';
      case 'CANCELLED':
        return 'Conversion cancelled';
      default:
        return '';
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <DocumentArrowUpIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">File Converter</h1>
          </div>
          <p className="text-gray-600">
            Upload your payroll files to convert them to V3 Payroll Standard
            format
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - File Upload */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                Upload Files
              </h2>
              <FileUploader
                onFilesSelected={handleFilesSelected}
                isProcessing={isProcessing}
                maxFiles={10}
              />
            </div>

            {/* Active Conversions */}
            {activeConversions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 bg-white shadow rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-medium text-gray-900">
                    Active Conversions ({activeConversions.length})
                  </h2>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveConversions(prev =>
                        prev.filter(
                          ac =>
                            ac.conversion.status === 'PENDING' ||
                            ac.conversion.status === 'PROCESSING'
                        )
                      )
                    }
                    className="text-sm text-gray-600 hover:text-gray-700"
                  >
                    Clear Completed
                  </button>
                </div>

                <div className="space-y-4">
                  <AnimatePresence>
                    {activeConversions.map(({ conversion, file }) => (
                      <motion.div
                        key={conversion.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              Source: {conversion.source_system}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {conversion.status === 'COMPLETED' && (
                              <button
                                type="button"
                                onClick={() => handleDownload(conversion.id)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Download converted file"
                              >
                                <ArrowDownTrayIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleViewStatus(conversion.id)}
                              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        </div>

                        <ConversionProgress
                          progress={getConversionProgress(conversion)}
                          status={conversion.status}
                          statusMessage={getStatusMessage(conversion)}
                          onCancel={
                            conversion.status === 'PENDING' ||
                            conversion.status === 'PROCESSING'
                              ? () => handleCancelConversion(conversion.id)
                              : undefined
                          }
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Info & Tips */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Quick Stats
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Active Conversions
                  </span>
                  <span className="text-lg font-semibold text-blue-600">
                    {
                      activeConversions.filter(
                        ac =>
                          ac.conversion.status === 'PENDING' ||
                          ac.conversion.status === 'PROCESSING'
                      ).length
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="text-lg font-semibold text-green-600">
                    {
                      activeConversions.filter(
                        ac => ac.conversion.status === 'COMPLETED'
                      ).length
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Failed</span>
                  <span className="text-lg font-semibold text-red-600">
                    {
                      activeConversions.filter(
                        ac => ac.conversion.status === 'FAILED'
                      ).length
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Supported File Types */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Supported File Types
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>CSV (.csv)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Excel (.xlsx, .xls)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>JSON (.json)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>XML (.xml)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Text (.txt)</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-800">
                  Maximum file size: 50 MB per file
                </p>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
              <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                <ClockIcon className="h-4 w-4 mr-2 text-blue-600" />
                Pro Tips
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>
                    Upload multiple files at once for batch processing
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>
                    Use custom mapping configurations for better accuracy
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Conversion typically takes 10-30 seconds per file</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>You can monitor progress in real-time</span>
                </li>
              </ul>
            </div>

            {/* Source Systems */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                Supported Source Systems
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {['QuickBooks', 'ADP', 'Paychex', 'Gusto', 'Workday'].map(
                  system => (
                    <div
                      key={system}
                      className="px-3 py-2 bg-gray-50 rounded-lg text-center text-xs font-medium text-gray-700"
                    >
                      {system}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Status Modal */}
      {selectedConversionId && (
        <ConversionStatusModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedConversionId(null);
          }}
          conversionId={selectedConversionId}
          onDownload={handleDownload}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
}
